import type { GraphQLError } from 'graphql';

import { fg } from '@atlaskit/platform-feature-flags/fg';

/**
 * Errors come in in with type unknown, but from the Sink interface:
 *
 * An error that has occured. Calling this function "closes" the sink.
 * Besides the errors being `Error` and `readonly GraphQLError[]`, it
 * can also be a `CloseEvent`, but to avoid bundling DOM typings because
 * the client can run in Node env too, you should assert the close event
 * type during implementation.
 *
 * error(error: unknown): void;
 */

/**
 * The shapes the client is known to send. `getSinkError` accepts `unknown`, because the
 * `Sink` interface promises nothing beyond that, and returns a result for anything.
 */
export type SinkError = Error | readonly GraphQLError[] | CloseEvent | ErrorEvent;

/**
 * A plain `Error`, optionally carrying the close information of the `CloseEvent` it came from.
 *
 * `closeCode` and `wasClean` are set if, and only if, the input was a `CloseEvent`, so their
 * absence is a fact about the input. `closeCode` is the raw WebSocket/`graphql-ws` close code,
 * which lets callers tell a permanent failure from a retryable one without matching on `message`.
 *
 * Not named `code`: the `Error` branch below passes its input through untouched, and those errors
 * routinely carry an unrelated `code` of their own (`ECONNRESET` and friends), which would leave a
 * caller unable to tell the two apart.
 */
export interface SinkErrorWithCloseInfo extends Error {
	closeCode?: number;
	wasClean?: boolean;
}

/**
 * Discriminates how a `SinkError` arrived, so consumers can branch on a stable field
 * instead of substring-matching `message`.
 *
 * - `graphql-errors`: an array payload of `GraphQLError`s.
 * - `close-event`: a WebSocket `CloseEvent`.
 * - `error-event`: a WebSocket `ErrorEvent`.
 * - `event`: anything else carrying a string `type` (for example a generic `disconnect` event).
 * - `error`: an `Error` instance passed straight through.
 * - `unknown`: anything else (string, untyped object, null, undefined).
 */
export type SinkErrorKind =
	| 'graphql-errors'
	| 'close-event'
	| 'error-event'
	| 'event'
	| 'error'
	| 'unknown';

/** Extra fields on the Error; `message` stays for existing matchers. */
export type SinkErrorDetails = {
	kind: SinkErrorKind;
	/** Full array payload; `message` still comes from `[0]`. */
	graphQLErrors?: readonly GraphQLError[];
};

export type StructuredSinkError = SinkErrorWithCloseInfo & SinkErrorDetails;

/** `Array.isArray` alone narrows a `readonly` array to `any[]`, losing the element type. */
const isGraphQLErrorArray = (error: unknown): error is readonly GraphQLError[] =>
	Array.isArray(error);

/**
 * Events are duck-typed rather than matched with `instanceof`: `Event`, `CloseEvent` and
 * `ErrorEvent` are not globals in every environment the client runs in, and `instanceof`
 * throws outright when its right-hand side is undefined.
 */
const isEventLike = (error: unknown): error is { type: string } =>
	typeof error === 'object' && error !== null && 'type' in error && typeof error.type === 'string';

const isCloseEvent = (error: unknown): error is CloseEvent =>
	isEventLike(error) && error.type === 'close';

const isErrorEvent = (error: unknown): error is ErrorEvent =>
	isEventLike(error) && error.type === 'error';

/** Non-enumerable details on a shared Error instance (same object goes to every sink). */
const withDetails = <T extends Error>(
	error: T,
	details: SinkErrorDetails,
): T & SinkErrorDetails => {
	for (const [key, value] of Object.entries(details)) {
		Object.defineProperty(error, key, {
			value,
			enumerable: false,
			configurable: true,
			writable: true,
		});
	}
	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	return error as T & SinkErrorDetails;
};

/** Pre-gate sink shaping. */
const getLegacySinkError = (error: SinkError): SinkErrorWithCloseInfo => {
	let errorInfo: SinkErrorWithCloseInfo;
	if (Array.isArray(error)) {
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		[errorInfo] = error as readonly GraphQLError[];
	} else if (error instanceof Error) {
		errorInfo = error;
	} else if (typeof error === 'object' && 'type' in error && error.type === 'close') {
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		const closeEvent = error as CloseEvent;
		errorInfo = new Error(`CloseEvent: ${closeEvent.reason}`);
		errorInfo.closeCode = closeEvent.code;
		errorInfo.wasClean = closeEvent.wasClean;
	} else if (typeof error === 'object' && 'type' in error && error.type === 'error') {
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		const errorEvent = error as ErrorEvent;
		errorInfo = new Error(`ErrorEvent: "${errorEvent.error}"`);
	} else if (error instanceof Event) {
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		errorInfo = new Error(`Event: ${(error as Event).type}`);
	} else {
		errorInfo = new Error(`Unknown sink error: ${error}`);
	}

	if (error instanceof Error && error.stack) {
		errorInfo.stack = error.stack;
	}

	return errorInfo;
};

const getStructuredSinkError = (error: unknown): StructuredSinkError => {
	if (isGraphQLErrorArray(error)) {
		const [firstError] = error;
		// A real Error, not the raw payload element typed as one, so it carries a stack. AGG
		// sends these as JSON, so the element is a plain object however it is typed.
		const errorInfo = Object.assign(new Error(firstError?.message), {
			kind: 'graphql-errors' as const,
			graphQLErrors: error,
		});
		if (firstError?.stack) {
			errorInfo.stack = firstError.stack;
		}
		return errorInfo;
	}

	if (error instanceof Error) {
		return withDetails(error, { kind: 'error' as const });
	}

	if (isCloseEvent(error)) {
		return Object.assign(new Error(`CloseEvent: ${error.reason}`), {
			kind: 'close-event' as const,
			closeCode: error.code,
			wasClean: error.wasClean,
		});
	}

	if (isErrorEvent(error)) {
		return Object.assign(new Error(`ErrorEvent: "${error.error}"`), {
			kind: 'error-event' as const,
		});
	}

	if (isEventLike(error)) {
		return Object.assign(new Error(`Event: ${error.type}`), { kind: 'event' as const });
	}

	// `String` rather than interpolation, which throws for a symbol.
	return Object.assign(new Error(`Unknown sink error: ${String(error)}`), {
		kind: 'unknown' as const,
	});
};

export const getSinkError = (error: unknown): StructuredSinkError => {
	if (fg('platform_relay_subscription_sink_error')) {
		return getStructuredSinkError(error);
	}
	// Gate off: legacy shape (no `kind`).
	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	return getLegacySinkError(error as SinkError) as StructuredSinkError;
};
